import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cachedFetch, getCachedData } from "../utils/apiCache";

import { useProfile } from "./useProfile";

/**
 * Enhanced landing page hook that manages onboarding completion state
 * and provides tab navigation helpers for completed users
 */
export const useLandingPage = () => {
  const [isUpdatingFlag, setIsUpdatingFlag] = useState(false);
  const queryClient = useQueryClient();

  // Leverage existing profile hook
  const {
    data: profileData,
    isLoading: profileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useProfile();

  // Use Query for landing flag
  const {
    data: landingData,
    isLoading: landingQueryLoading,
    error: landingError,
    refetch: refetchLanding,
  } = useQuery({
    queryKey: ["landing-flag"],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const res = await cachedFetch("/api/landing/get", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch landing flag");
      return await res.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    initialData: () => {
      const cached = getCachedData("/api/landing/get");
      return cached === null ? undefined : cached;
    },
  });

  // Derived state
  const landingFlag = landingData?.landingFlag ?? null;
  const isOnboardingComplete = landingFlag === 4; // 4 means all steps completed
  const isLoading = landingQueryLoading || profileLoading;
  const error = landingError || profileError;

  // Update landing flag
  const updateLandingFlag = async (landingFlag) => {
    try {
      setIsUpdatingFlag(true);
      const token = localStorage.getItem("token");
      const res = await fetch("/api/landing/update", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ landingFlag }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || "Failed to update landing flag");
      }
      const result = await res.json();
      
      // Invalidate cache and refetch both
      await Promise.all([
        queryClient.invalidateQueries(["landing-flag"]),
        refetchProfile()
      ]);
      
      return result;
    } finally {
      setIsUpdatingFlag(false);
    }
  };

  // Get completion status for each section based on landing flag
  const getCompletionStatus = () => {
    // If landingFlag is 4, all sections are complete
    if (landingFlag === 4) {
      return {
        basicInfo: true,
        workHistory: true,
        education: true,
        summary: true,
      };
    }

    // Otherwise, determine completion based on current step
    return {
      basicInfo: landingFlag >= 1, // Completed step 0 (basic info)
      workHistory: landingFlag >= 2, // Completed step 1 (work history)
      education: landingFlag >= 3, // Completed step 2 (education)
      summary: landingFlag >= 4, // Completed step 3 (summary)
    };
  };

  // Get current step based on completion status
  const getCurrentStep = () => {
    if (landingFlag === 4) return 4; // All complete
    if (landingFlag === 3) return 3; // Summary step
    if (landingFlag === 2) return 2; // Education step
    if (landingFlag === 1) return 1; // Work history step
    if (landingFlag === 0) return 0; // Basic info step

    // Fallback: determine from completion status
    const completion = getCompletionStatus();
    if (!completion.basicInfo) return 0;
    if (!completion.workHistory) return 1;
    if (!completion.education) return 2;
    if (!completion.summary) return 3;
    return 4;
  };

  // Tab configuration for completed users
  const getTabConfig = () => {
    const completion = getCompletionStatus();
    const baseTabs = [
      {
        label: "Basic Info",
        key: "basicInfo",
        route: "/me/profile/basic-info",
        stepperRoute: "/me",
        completed: completion.basicInfo,
      },
      {
        label: "Work History",
        key: "workHistory",
        route: "/me/profile/work-history",
        stepperRoute: "/me/work-experience",
        completed: completion.workHistory,
      },
      {
        label: "Education & Projects",
        key: "education",
        route: "/me/profile/education",
        stepperRoute: "/me/education-projects",
        completed: completion.education,
      },
      {
        label: "Summary & Skills",
        key: "summary",
        route: "/me/profile/summary",
        stepperRoute: "/me/summary-skills",
        completed: completion.summary,
      },
    ];

    // Add Resume Preview tab only when profile is complete
    if (isOnboardingComplete) {
      baseTabs.push({
        label: "Resume Preview",
        key: "resumePreview",
        route: "/me/profile/resume-preview",
        stepperRoute: null, // Not available in stepper mode
        completed: true,
      });
    }

    return baseTabs;
  };

  // Navigation helpers
  const getActiveTabFromPath = (pathname) => {
    if (pathname.includes("/basic-info") || pathname === "/me") return 0;
    if (pathname.includes("/work-history") || pathname.includes("/work12"))
      return 1;
    if (pathname.includes("/education") || pathname.includes("/edu")) return 2;
    if (pathname.includes("/summary") || pathname.includes("/ss")) return 3;
    if (pathname.includes("/resume-preview")) return 4;
    return 0;
  };

  const canAccessTab = (tabIndex) => {
    if (isOnboardingComplete) return true; // All tabs accessible when complete

    // During onboarding, can only access current step or previous completed steps
    const currentStep = getCurrentStep();
    return tabIndex <= currentStep;
  };

  // Get next route based on current completion
  const getNextRoute = () => {
    const currentStep = getCurrentStep();
    const tabConfig = getTabConfig();

    if (currentStep >= tabConfig.length) return "/me/MasterResume";

    return isOnboardingComplete
      ? tabConfig[currentStep]?.route
      : tabConfig[currentStep]?.stepperRoute;
  };

  return {
    // Core state
    landingFlag,
    isOnboardingComplete,
    isLoading,
    error,
    profileData,

    // Completion tracking
    completionStatus: getCompletionStatus(),
    currentStep: getCurrentStep(),

    // Navigation helpers
    tabConfig: getTabConfig(),
    getActiveTabFromPath,
    canAccessTab,
    getNextRoute,

    // Actions
    updateLandingFlag: (flag) => updateLandingFlag(flag),
    updateLandingFlagAsync: updateLandingFlag,
    isUpdatingFlag,

    // Refresh functions
    refetch: () => {
      refetchLanding();
      refetchProfile();
    },
  };
};

// Helper hook for unsaved changes detection
export const useUnsavedChanges = (
  hasChanges,
  message = "You have unsaved changes. Are you sure you want to leave?",
) => {
  const { useEffect } = require("react");

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    if (hasChanges) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasChanges, message]);
};
